import { useEffect, useRef, useState, type CSSProperties } from 'react';
import type { ThemePalette } from '../../types';
import {
  formatAttribute,
  formatMethod,
  parseAttribute,
  parseMethod,
  UML_FIELD_TYPES,
  UML_VISIBILITY,
  umlTypeShort,
  type UmlVisibility,
} from '../../utils/umlMembers';

interface UmlMemberEditorProps {
  mode: 'field' | 'method';
  lines: string[];
  allowType: boolean;
  palette: Pick<ThemePalette, 'panelText' | 'panelSubText' | 'panelBorder'>;
  onChange: (lines: string[]) => void;
}

type OpenMenu = { index: number; kind: 'vis' | 'type' } | null;

const VIS_COLOR: Record<UmlVisibility, string> = {
  '+': '#34d399',
  '-': '#f87171',
  '#': '#fbbf24',
  '~': '#94a3b8',
};

const TYPE_CHOICES = UML_FIELD_TYPES.filter(t => t !== 'void');
const RETURN_CHOICES = UML_FIELD_TYPES;

function TrashIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path d="M6 2h4M3 4h10M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 7v4M9 7v4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden>
      <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.4" />
      <path d="M8 7.2V11.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="8" cy="5.1" r="0.8" fill="currentColor" />
    </svg>
  );
}

export function UmlMemberEditor({ mode, lines, allowType, palette, onChange }: UmlMemberEditorProps) {
  const field = palette.panelText;
  const muted = palette.panelSubText;
  const border = palette.panelBorder;
  const [open, setOpen] = useState<OpenMenu>(null);
  const [customType, setCustomType] = useState<{ index: number; value: string } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) setOpen(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const update = (index: number, next: string) => {
    const copy = [...lines];
    copy[index] = next;
    onChange(copy);
  };

  const remove = (index: number) => {
    setOpen(null);
    onChange(lines.filter((_, i) => i !== index));
  };

  const add = () => {
    if (mode === 'method') {
      onChange([...lines, formatMethod({ visibility: '+', name: 'method', params: '', returnType: 'void' })]);
      return;
    }
    onChange([...lines, formatAttribute({
      visibility: '+',
      name: allowType ? 'field' : 'VALUE',
      type: allowType ? 'string' : '',
      optionality: 'required',
    })]);
  };

  const popover: CSSProperties = {
    background: '#0f172a',
    border: '1px solid #475569',
    color: '#f8fafc',
    boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
  };

  return (
    <div ref={rootRef} className="space-y-1.5">
      <div className="relative flex items-center gap-1.5 mb-1">
        <button
          type="button"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full border"
          style={{ color: muted, borderColor: border }}
          title={UML_VISIBILITY.map(v => `${v.value} ${v.label}`).join(' · ')}
          aria-label="Visibility legend"
          onClick={() => setOpen(open?.kind === 'vis' && open.index === -1 ? null : { index: -1, kind: 'vis' })}
        >
          <InfoIcon />
        </button>
        {open?.kind === 'vis' && open.index === -1 && (
          <div className="absolute left-6 top-0 z-50 rounded-md px-2 py-1.5 text-[11px] leading-5" style={popover}>
            {UML_VISIBILITY.map(v => (
              <div key={v.value} className="flex items-center gap-2">
                <span className="w-3 text-center font-semibold" style={{ color: VIS_COLOR[v.value] }}>{v.value}</span>
                <span>{v.label}</span>
              </div>
            ))}
          </div>
        )}
        <span className="sr-only">Visibility: + public, − private, # protected, ~ package</span>
      </div>

      {lines.map((line, index) => {
        if (mode === 'method') {
          const m = parseMethod(line);
          const knownRet = (UML_FIELD_TYPES as readonly string[]).includes(m.returnType);
          return (
            <div key={index} className="flex items-center gap-1.5 min-w-0">
              <VisChip
                value={m.visibility}
                open={open?.index === index && open.kind === 'vis'}
                onToggle={() => setOpen(open?.index === index && open.kind === 'vis' ? null : { index, kind: 'vis' })}
                onPick={vis => {
                  update(index, formatMethod({ ...m, visibility: vis }));
                  setOpen(null);
                }}
                popover={popover}
              />
              <input
                className="bg-white/5 border rounded px-1.5 py-1 text-[11px] focus:outline-none focus:border-indigo-500/60 min-w-0 flex-1"
                style={{ color: field, borderColor: border }}
                value={m.name}
                placeholder="name"
                onChange={e => update(index, formatMethod({ ...m, name: e.target.value }))}
              />
              <input
                className="bg-white/5 border rounded px-1.5 py-1 text-[11px] focus:outline-none focus:border-indigo-500/60 w-16 shrink-0"
                style={{ color: field, borderColor: border }}
                value={m.params}
                placeholder="args"
                onChange={e => update(index, formatMethod({ ...m, params: e.target.value }))}
              />
              {allowType && (
                <TypeChip
                  value={m.returnType}
                  choices={RETURN_CHOICES}
                  open={open?.index === index && open.kind === 'type'}
                  customOpen={customType?.index === index}
                  customValue={customType?.index === index ? customType.value : m.returnType}
                  fieldColor={field}
                  border={border}
                  popover={popover}
                  onToggle={() => {
                    setCustomType(null);
                    setOpen(open?.index === index && open.kind === 'type' ? null : { index, kind: 'type' });
                  }}
                  onPick={type => {
                    update(index, formatMethod({ ...m, returnType: type }));
                    setOpen(null);
                    setCustomType(null);
                  }}
                  onCustom={() => {
                    setCustomType({ index, value: knownRet ? '' : m.returnType });
                    setOpen(null);
                  }}
                  onCustomChange={value => {
                    setCustomType({ index, value });
                    update(index, formatMethod({ ...m, returnType: value || 'T' }));
                  }}
                />
              )}
              <TrashButton muted={muted} onClick={() => remove(index)} />
            </div>
          );
        }

        const a = parseAttribute(line);
        const knownType = !a.type || (UML_FIELD_TYPES as readonly string[]).includes(a.type);
        return (
          <div key={index} className="flex items-center gap-1.5 min-w-0">
            <VisChip
              value={a.visibility}
              open={open?.index === index && open.kind === 'vis'}
              onToggle={() => setOpen(open?.index === index && open.kind === 'vis' ? null : { index, kind: 'vis' })}
              onPick={vis => {
                update(index, formatAttribute({ ...a, visibility: vis }));
                setOpen(null);
              }}
              popover={popover}
            />
            <input
              className="bg-white/5 border rounded px-1.5 py-1 text-[11px] focus:outline-none focus:border-indigo-500/60 min-w-0 flex-1"
              style={{ color: field, borderColor: border }}
              value={a.name}
              placeholder="name"
              onChange={e => update(index, formatAttribute({ ...a, name: e.target.value }))}
            />
            {allowType && (
              <>
                <TypeChip
                  value={a.type || 'string'}
                  choices={TYPE_CHOICES}
                  open={open?.index === index && open.kind === 'type'}
                  customOpen={customType?.index === index}
                  customValue={customType?.index === index ? customType.value : a.type}
                  fieldColor={field}
                  border={border}
                  popover={popover}
                  onToggle={() => {
                    setCustomType(null);
                    setOpen(open?.index === index && open.kind === 'type' ? null : { index, kind: 'type' });
                  }}
                  onPick={type => {
                    update(index, formatAttribute({ ...a, type }));
                    setOpen(null);
                    setCustomType(null);
                  }}
                  onCustom={() => {
                    setCustomType({ index, value: knownType ? '' : a.type });
                    setOpen(null);
                  }}
                  onCustomChange={value => {
                    setCustomType({ index, value });
                    update(index, formatAttribute({ ...a, type: value || 'T' }));
                  }}
                />
                <label className="flex items-center shrink-0" title="Required">
                  <input
                    type="checkbox"
                    className="accent-indigo-400 w-3.5 h-3.5"
                    checked={a.optionality !== 'optional'}
                    onChange={e => update(index, formatAttribute({
                      ...a,
                      optionality: e.target.checked ? 'required' : 'optional',
                    }))}
                    aria-label="Required"
                  />
                </label>
              </>
            )}
            <TrashButton muted={muted} onClick={() => remove(index)} />
          </div>
        );
      })}
      <button
        type="button"
        className="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors"
        onClick={add}
      >
        + Add {mode === 'method' ? 'method' : 'field'}
      </button>
    </div>
  );
}

function TrashButton({ muted, onClick }: { muted: string; onClick: () => void }) {
  return (
    <button
      type="button"
      className="shrink-0 p-1 rounded hover:bg-white/10"
      style={{ color: muted }}
      onClick={onClick}
      aria-label="Remove"
    >
      <TrashIcon />
    </button>
  );
}

function VisChip({
  value,
  open,
  onToggle,
  onPick,
  popover,
}: {
  value: UmlVisibility;
  open: boolean;
  onToggle: () => void;
  onPick: (value: UmlVisibility) => void;
  popover: CSSProperties;
}) {
  return (
    <div className="relative shrink-0">
      <button
        type="button"
        className="w-6 h-7 rounded text-sm font-semibold leading-none"
        style={{ color: VIS_COLOR[value], background: 'rgba(255,255,255,0.06)' }}
        onClick={onToggle}
        aria-label={`Visibility ${value}`}
        aria-expanded={open}
      >
        {value}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 flex gap-1 rounded-md p-1" style={popover} role="listbox">
          {UML_VISIBILITY.map(v => (
            <button
              key={v.value}
              type="button"
              className="w-7 h-7 rounded text-sm font-semibold hover:bg-white/10"
              style={{ color: VIS_COLOR[v.value], background: v.value === value ? 'rgba(255,255,255,0.12)' : 'transparent' }}
              onClick={() => onPick(v.value)}
              aria-label={v.label}
              title={v.label}
            >
              {v.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TypeChip({
  value,
  choices,
  open,
  customOpen,
  customValue,
  fieldColor,
  border,
  popover,
  onToggle,
  onPick,
  onCustom,
  onCustomChange,
}: {
  value: string;
  choices: readonly string[];
  open: boolean;
  customOpen: boolean;
  customValue: string;
  fieldColor: string;
  border: string;
  popover: CSSProperties;
  onToggle: () => void;
  onPick: (type: string) => void;
  onCustom: () => void;
  onCustomChange: (value: string) => void;
}) {
  if (customOpen) {
    return (
      <input
        autoFocus
        className="bg-white/5 border rounded px-1.5 py-1 text-[11px] w-14 shrink-0 focus:outline-none focus:border-indigo-500/60"
        style={{ color: fieldColor, borderColor: border }}
        value={customValue}
        placeholder="type"
        onChange={e => onCustomChange(e.target.value)}
        onBlur={() => undefined}
      />
    );
  }

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        className="min-w-[2.25rem] h-7 px-1.5 rounded text-[11px] font-medium"
        style={{ color: fieldColor, background: 'rgba(255,255,255,0.06)' }}
        onClick={onToggle}
        aria-label={`Type ${value}`}
        aria-expanded={open}
      >
        {umlTypeShort(value)}
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[7rem] rounded-md py-1" style={popover} role="listbox">
          {choices.map(type => (
            <button
              key={type}
              type="button"
              className="flex w-full items-center justify-between px-2.5 py-1 text-left text-[12px] hover:bg-white/10"
              style={{ color: '#f8fafc', background: type === value ? 'rgba(255,255,255,0.08)' : 'transparent' }}
              onClick={() => onPick(type)}
            >
              <span>{umlTypeShort(type)}</span>
              <span className="text-[10px] text-slate-400">{type}</span>
            </button>
          ))}
          <button
            type="button"
            className="flex w-full px-2.5 py-1 text-left text-[12px] text-slate-200 hover:bg-white/10"
            onClick={onCustom}
          >
            custom…
          </button>
        </div>
      )}
    </div>
  );
}
